import { useState, useEffect } from 'react';
import { useAppContext } from '@/App';
import { getMyDashboard } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Users, FileText, ArrowRight } from 'lucide-react';

export default function InstructorDashboard() {
  const { currentInstructor } = useAppContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const instructorId = currentInstructor?.id;

  useEffect(() => {
    if (!instructorId) return;

    let cancelled = false;

    const loadData = async () => {
      try {
        setLoading(true);
        const res = await getMyDashboard();
        if (!cancelled) {
          setData(res.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [instructorId]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-28 bg-white rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          Welcome, {currentInstructor?.name?.split(' ')[0]}
        </h2>
        <p className="text-sm text-[#062B1A]/60 mt-1">{currentInstructor?.department}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">My Courses</span>
              <BookOpen className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{data?.courses?.length || 0}</p>
          </CardContent>
        </Card>
        <Card className="card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm gold-border-left">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Total Students</span>
              <Users className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{data?.total_students || 0}</p>
          </CardContent>
        </Card>
        <Card className="card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Pending Reviews</span>
              <FileText className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{data?.pending_submissions?.length || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Courses list */}
      <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>My Courses</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {data?.courses?.length === 0 ? (
            <p className="text-sm text-[#062B1A]/50 text-center py-6">No courses assigned</p>
          ) : (
            data?.courses?.map(course => (
              <div key={course.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F7F8FA] hover:bg-[rgba(212, 175, 55,0.06)] transition-colors cursor-pointer" onClick={() => navigate('/instructor/courses')}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#062B1A] flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#062B1A]">{course.title}</p>
                    <div className="flex items-center gap-2 text-xs text-[#062B1A]/40">
                      <span>{course.enrollment_count} students</span>
                      <span>•</span>
                      <Badge variant="outline" className="text-[10px]">{course.status}</Badge>
                    </div>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#062B1A]/30" />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Pending submissions */}
      {data?.pending_submissions?.length > 0 && (
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Pending Reviews</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Each row names the work and who handed it in, and opens the
                course it belongs to — the marking happens alongside the item. */}
            {data.pending_submissions.map(sub => (
              <button
                key={sub.id}
                type="button"
                disabled={!sub.course_id}
                onClick={() => sub.course_id && navigate(`/instructor/courses/${sub.course_id}`)}
                className="flex w-full items-center justify-between gap-3 p-3 rounded-lg bg-[#F7F8FA] text-left transition-colors hover:bg-[#EEF0F4] disabled:cursor-default disabled:hover:bg-[#F7F8FA]"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Badge variant="outline" className="shrink-0 text-[10px] capitalize">{sub.type}</Badge>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-[#062B1A]">
                      {sub.item_title || 'Untitled'}
                    </span>
                    <span className="block truncate text-xs text-[#062B1A]/50">
                      {sub.student_name || 'Student'}
                    </span>
                  </span>
                </div>
                <Badge className="shrink-0 bg-yellow-100 text-yellow-700 border-0 text-[10px]">Pending</Badge>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
